import { IAPIRoutes } from "../../../../config";
import type {
  INeoTicketRepository,
  NeoTicket,
  NeoTicketBlock,
  NeoTicketCreateRequest,
  NeoTicketListOptions,
  NeoTicketListResponse,
  NeoTicketPatchRequest,
} from "../../../core/ports/driven/neoTicketRepository";

const fillRoute = (
  template: string,
  params: Record<string, string | number>
): string => {
  let out = template;
  for (const [key, value] of Object.entries(params)) {
    out = out.replace(`{${key}}`, String(value));
  }
  return out;
};

export type INeoTicketRepositoryFactory = ReturnType<
  typeof NeoTicketRepositoryFactory
>;

export const NeoTicketRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}): INeoTicketRepository => {
  const { GET, POST, PATCH, DELETE } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const authHeader = async () => ({
    Authorization: `Bearer ${await getAuthenticationToken()}`,
  });

  const listTicketsAPI = async (
    projectId: number,
    opts?: NeoTicketListOptions
  ): Promise<NeoTicketListResponse> => {
    const params: Record<string, string | number> = {
      limit: opts?.limit ?? 50,
    };
    if (opts?.offset) params.offset = opts.offset;
    if (opts?.status) params.status = opts.status;
    return GET(
      fillRoute(APIRoutes.apiNeoProjectTickets, { project_id: projectId }),
      params,
      await authHeader()
    );
  };

  const getTicketAPI = async (
    projectId: number,
    ticketId: number
  ): Promise<NeoTicket> =>
    GET(
      fillRoute(APIRoutes.apiNeoProjectTicket, {
        project_id: projectId,
        ticket_id: ticketId,
      }),
      {},
      await authHeader()
    );

  const createTicketAPI = async (
    projectId: number,
    payload: NeoTicketCreateRequest
  ): Promise<NeoTicket> =>
    POST(
      fillRoute(APIRoutes.apiNeoProjectTickets, { project_id: projectId }),
      payload,
      await authHeader()
    );

  const updateTicketAPI = async (
    projectId: number,
    ticketId: number,
    payload: NeoTicketPatchRequest
  ): Promise<NeoTicket> =>
    PATCH(
      fillRoute(APIRoutes.apiNeoProjectTicket, {
        project_id: projectId,
        ticket_id: ticketId,
      }),
      payload,
      await authHeader()
    );

  const appendTicketBlocksAPI = async (
    projectId: number,
    ticketId: number,
    blocks: NeoTicketBlock[]
  ): Promise<NeoTicket> =>
    POST(
      fillRoute(APIRoutes.apiNeoProjectTicketBlocks, {
        project_id: projectId,
        ticket_id: ticketId,
      }),
      { blocks },
      await authHeader()
    );

  const cancelTicketAPI = async (
    projectId: number,
    ticketId: number
  ): Promise<NeoTicket> =>
    POST(
      fillRoute(APIRoutes.apiNeoProjectTicketCancel, {
        project_id: projectId,
        ticket_id: ticketId,
      }),
      {},
      await authHeader()
    );

  const deleteTicketAPI = async (
    projectId: number,
    ticketId: number
  ): Promise<void> => {
    await DELETE(
      fillRoute(APIRoutes.apiNeoProjectTicket, {
        project_id: projectId,
        ticket_id: ticketId,
      }),
      {},
      await authHeader()
    );
  };

  return {
    listTicketsAPI,
    getTicketAPI,
    createTicketAPI,
    updateTicketAPI,
    appendTicketBlocksAPI,
    cancelTicketAPI,
    deleteTicketAPI,
  };
};
